/**
 * AuthVerify — handles the magic link callback.
 *
 * When the user clicks the magic link in their email, they're directed to:
 *   /auth/verify?token=<token>
 *
 * This component:
 * 1. Extracts the token from the URL
 * 2. Shows a "Sign in" button (does NOT auto-verify on page load)
 * 3. When the user clicks, calls POST /api/auth/verify-magic-link with the token
 * 4. On success: stores the JWT via AuthContext and shows success
 * 5. On error: shows the error message
 *
 * The explicit click requirement prevents email URL scanners (e.g. Outlook
 * Safe Links) from consuming the single-use token before the real user
 * can click it.
 *
 * After verification, redirects to the main app by clearing the URL hash.
 */

import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getApiUrl } from '../utils/environment';
import styles from './Login.module.css';

interface AuthVerifyProps {
  /** The token from the URL */
  token: string;
  /** Called when verification completes (success or user dismisses error) */
  onComplete: () => void;
}

type VerifyStatus = 'ready' | 'verifying' | 'success' | 'error';

function getApiBaseUrl(): string {
  return getApiUrl();
}

export function AuthVerify({ token, onComplete }: AuthVerifyProps) {
  const { login } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>('ready');
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    setStatus('verifying');
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/auth/verify-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Verification failed');
        setStatus('error');
        return;
      }

      // Store JWT and user via auth context
      login(data.jwt, data.user, data.cfCookies);
      setStatus('success');

      // Auto-redirect after brief delay
      setTimeout(() => onComplete(), 1500);
    } catch {
      setError('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>🔥</div>
        <h1 className={styles.title}>Talkyn</h1>

        {status === 'ready' && (
          <>
            <p className={styles.subtitle}>Click below to complete sign-in</p>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleVerify}
            >
              Sign in to Talkyn
            </button>
            <p className={styles.sentHint} style={{ marginTop: '16px' }}>
              This extra step keeps your account safe from email scanners.
            </p>
          </>
        )}

        {status === 'verifying' && (
          <>
            <p className={styles.subtitle}>Verifying your magic link…</p>
            <div style={{ fontSize: '32px', marginTop: '16px' }}>⏳</div>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <p className={styles.subtitle}>You're signed in!</p>
            <p className={styles.sentHint}>Redirecting to the app…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
            <p className={styles.subtitle}>Verification failed</p>
            <div className={styles.error} style={{ marginBottom: '16px' }}>
              {error}
            </div>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onComplete}
            >
              Continue to app
            </button>
          </>
        )}
      </div>
    </div>
  );
}
